/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import { useCallback, useEffect, useRef } from 'react';

/**
 * Warn before a half-filled form is thrown away.
 *
 * Two different exits need two different mechanisms, because the browser only
 * lets a page argue about one of them:
 *
 *   • **Refresh, close, typed URL** — `beforeunload`. The browser shows its own
 *     dialog and ignores whatever text a page supplies; a plain `confirm()` or
 *     `alert()` here does nothing at all, since the page has already been told
 *     it is going away. Setting `returnValue` is the entire API.
 *
 *   • **In-app navigation** — a click on any link inside the app. React Router
 *     never leaves the document, so `beforeunload` never fires. `useBlocker`
 *     would be the natural answer but it needs a data router, and this app is
 *     mounted on `BrowserRouter`; converting the whole shell to a data router
 *     to guard one wizard is not a trade worth making. Instead the click is
 *     caught during the capture phase — before React Router's own handler sees
 *     it — and stopped there if the admin decides to stay. That covers the
 *     sidebar, the header and the breadcrumbs alike, since all of them navigate
 *     through `<Link>`, which renders a real anchor.
 *
 * The browser Back button is not covered: cancelling a history entry requires
 * pushing a decoy state and un-pushing it, which corrupts the history stack for
 * everything downstream. Refresh, close and every in-app link are.
 *
 * @param {boolean} dirty whether the form holds unsaved work
 * @param {string} message shown for in-app navigation (browsers substitute
 *   their own wording for refresh and close)
 * @returns {() => boolean} `confirmLeave` — call it before navigating on
 *   purpose (a Cancel button, a breadcrumb); true means "go ahead"
 */
export default function useUnsavedChangesGuard(dirty, message) {
  // Read through a ref inside the listeners so the handlers stay stable and the
  // guard does not detach and reattach on every keystroke.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const messageRef = useRef(message);
  messageRef.current = message;

  const confirmLeave = useCallback(() => {
    if (!dirtyRef.current) return true;
    return window.confirm(messageRef.current);
  }, []);

  useEffect(() => {
    function onBeforeUnload(event) {
      if (!dirtyRef.current) return;
      event.preventDefault();
      // Required by older browsers; modern ones only check that it was set.
      event.returnValue = '';
    }

    function onClickCapture(event) {
      if (!dirtyRef.current) return;
      // Only a plain left click actually navigates. Ctrl/Cmd/middle-click opens
      // a new tab and leaves this page — and its form — exactly where it is.
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = event.target.closest?.('a[href]');
      if (!anchor) return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;

      const href = anchor.getAttribute('href');
      // An in-page jump ("#section") is not leaving anywhere.
      if (!href || href.startsWith('#')) return;

      const destination = new URL(anchor.href, window.location.href);
      if (destination.pathname === window.location.pathname
        && destination.search === window.location.search) return;

      if (!window.confirm(messageRef.current)) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    window.addEventListener('beforeunload', onBeforeUnload);
    // Capture phase: React attaches its listeners at the root and would
    // navigate before a bubbling handler ever ran.
    document.addEventListener('click', onClickCapture, true);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClickCapture, true);
    };
  }, []);

  return confirmLeave;
}
